import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { tokens } from '../styles/tokens';
import { STRINGS } from '../data/strings';
import type {
  RecapArticle,
  RecapSituationLine,
  SarahRecapApi,
} from '../types';

import './sarah-speaker';
import './sarah-recap-demarches';
import './sarah-box-test';
import './sarah-recap-cerfa';
import './sarah-recap-medecins';
import './sarah-recap-ants';
import './sarah-recap-articles';
import './sarah-inscription';

export interface RecapBuilt {
  situation: RecapSituationLine[];
  articles: RecapArticle[];
}

/**
 * Message Sarah affiché en haut du récap (nœud `final` du dialogue d'origine).
 * On le duplique ici en fallback : si le parent passe `final-message`, il prime.
 */
const DEFAULT_FINAL_MESSAGE =
  "<p>Merci ! Vous allez recevoir par email un récapitulatif complet de votre test psychotechnique et de votre visite médicale.</p><p>À bientôt !</p>";

@customElement('sarah-recap')
export class SarahRecap extends LitElement {
  static styles = [
    tokens,
    css`
      :host {
        display: block;
        font-family: var(--sarah-font-body);
        color: var(--sarah-text);
      }

      .speaker-wrap { margin-bottom: 30px; }

      .recap { margin-top: 60px; }

      .recap .titre h2 {
        margin: 0;
        font-family: var(--sarah-font-display);
        font-weight: 700;
        font-size: 30px;
        text-align: left;
        color: var(--sarah-text);
      }
      @media (max-width: 767px) {
        .recap .titre h2 { font-size: 22px; }
      }

      .recap .titre.titre-image {
        display: flex;
        align-content: center;
        align-items: center;
        flex-direction: row;
        margin-bottom: 50px;
      }
      @media (max-width: 767px) {
        .recap .titre.titre-image { margin-bottom: 30px; }
      }
      .recap .titre.titre-image img { height: 70px; margin-right: 30px; }
      @media (max-width: 767px) {
        .recap .titre.titre-image img { height: 50px; }
      }
      .recap .titre.titre-image span { color: var(--sarah-rose); margin-right: 10px; }

      .recap-container {
        background: #fff;
        padding: 35px;
        border-radius: 20px;
        box-shadow: 0 1px 11px 2px #0000001a, 0 1px 2px 0 #0000000f;
        margin-bottom: 70px;
      }
      @media (max-width: 719px) {
        .recap-container { padding: 32px 22px; }
        .recap-container:not(:last-child) { margin-bottom: 30px; }
      }
      .recap-container:not(:first-child) { margin-top: 70px; }
      @media (max-width: 719px) {
        .recap-container:not(:first-child) { margin-top: 30px; }
      }

      .votre-situation ul {
        text-align: left;
        font-size: 18px;
        list-style-type: none;
        padding: 0 20px;
        margin-bottom: 0;
        line-height: 1.5;
      }
      @media (max-width: 767px) {
        .votre-situation ul { padding: 0 15px; }
      }
      .votre-situation ul li:before {
        content: "\\2713";
        padding-right: 10px;
        color: var(--sarah-bleu);
      }

      .legal {
        padding-top: 40px;
        padding-bottom: 30px;
        text-align: justify;
      }
      .legal p {
        color: #aaa8a1;
        font-size: 14px;
        line-height: 18px;
        margin: 0 auto;
      }
      .legal a { color: inherit; font-weight: 700; }

      .empty-tests { font-size: 16px; color: var(--sarah-text); margin: 0; }

      @keyframes recapFade {
        from { opacity: 0; transform: translateY(8px); }
        to   { opacity: 1; transform: translateY(0); }
      }

      .recap.recap-reveal .recap-container,
      .recap.recap-reveal .legal {
        opacity: 0;
        animation: recapFade .5s ease forwards;
      }
      .recap.recap-reveal .recap-container:nth-child(1) { animation-delay: 0ms; }
      .recap.recap-reveal .recap-container:nth-child(2) { animation-delay: 100ms; }
      .recap.recap-reveal .recap-container:nth-child(3) { animation-delay: 200ms; }
      .recap.recap-reveal .recap-container:nth-child(4) { animation-delay: 300ms; }
      .recap.recap-reveal .recap-container:nth-child(5) { animation-delay: 400ms; }
      .recap.recap-reveal .recap-container:nth-child(6) { animation-delay: 500ms; }
      .recap.recap-reveal .recap-container:nth-child(7) { animation-delay: 600ms; }
      .recap.recap-reveal .legal { animation-delay: 700ms; }

      @media (prefers-reduced-motion: reduce) {
        .recap.recap-reveal .recap-container,
        .recap.recap-reveal .legal {
          opacity: 1;
          animation: none;
        }
      }
    `,
  ];

  @property({ type: String }) prenom = '';
  @property({ type: Object }) recapApi: SarahRecapApi | null = null;
  @property({ type: Object }) recapBuilt: RecapBuilt | null = null;
  @property({ type: String, attribute: 'assets-base' }) assetsBase = 'assets';
  @property({ type: String, attribute: 'final-message' }) finalMessage = '';
  @property({ type: String, attribute: 'avatar-src' }) avatarSrc = '';

  @state() private speakerDone = false;

  private onTypingDone = (): void => {
    this.speakerDone = true;
  };

  render() {
    const api = this.recapApi || {};
    const built = this.recapBuilt || { situation: [], articles: [] };
    const tests = api.liste_tests || [];
    const medecins = api.medecins || [];
    const isPrefecture = !!api.medecins_prefecture;
    const medecinsTitle = isPrefecture
      ? STRINGS.medecinsTitlePrefecture
      : STRINGS.medecinsTitleAgrees;
    const stepIcon = `${this.assetsBase}/logo-analyse-etape1.png`;
    const message = this.finalMessage || DEFAULT_FINAL_MESSAGE;

    return html`
      <div class="speaker-wrap" @typing-done=${this.onTypingDone}>
        <sarah-speaker
          .message=${message}
          avatar-src=${this.avatarSrc || nothing}
        ></sarah-speaker>
      </div>

      ${this.speakerDone ? html`
      <div class="recap recap-reveal">
        <div class="recap-container votre-situation">
          <div class="titre"><h2>${STRINGS.situationTitle}</h2></div>
          <div class="contenu">
            <ul>
              ${built.situation.map((s) => html`<li>${s.recap}</li>`)}
            </ul>
          </div>
        </div>

        <div class="recap-container vos-demarches">
          <div class="titre"><h2>${STRINGS.demarchesTitle}</h2></div>
          <div class="contenu">
            <sarah-recap-demarches
              .demarches=${api.demarches || []}
            ></sarah-recap-demarches>
          </div>
        </div>

        <div class="recap-container vos-tests">
          <div class="titre titre-image">
            <img src=${stepIcon} alt="">
            <h2><span>Étape 1 : </span>${STRINGS.testsTitle}</h2>
          </div>
          <div class="contenu">
            ${tests.length === 0
              ? html`<p class="empty-tests">
                  Aucun test disponible pour le moment dans votre département.
                </p>`
              : tests.map(
                  (t) => html`<sarah-box-test .test=${t}></sarah-box-test>`,
                )}
          </div>
        </div>

        <div class="recap-container votre-cerfa">
          <div class="titre titre-image">
            <img src=${stepIcon} alt="">
            <h2><span>Étape 2 : </span>${STRINGS.cerfaTitle}</h2>
          </div>
          <div class="contenu">
            <sarah-recap-cerfa assets-base=${this.assetsBase}></sarah-recap-cerfa>
          </div>
        </div>

        <div class="recap-container vos-medecins">
          <div class="titre titre-image">
            <img src=${stepIcon} alt="">
            <h2><span>Étape 3 : </span>${medecinsTitle}</h2>
          </div>
          <div class="contenu">
            <sarah-recap-medecins
              .medecins=${medecins}
              ?is-prefecture=${isPrefecture}
            ></sarah-recap-medecins>
          </div>
        </div>

        <div class="recap-container votre-ants">
          <div class="titre titre-image">
            <img src=${stepIcon} alt="">
            <h2><span>Étape 4 : </span>${STRINGS.antsTitle}</h2>
          </div>
          <div class="contenu">
            <sarah-recap-ants assets-base=${this.assetsBase}></sarah-recap-ants>
          </div>
        </div>

        <div class="recap-container vos-articles">
          <div class="titre"><h2>${STRINGS.articlesTitle}</h2></div>
          <div class="contenu">
            <sarah-recap-articles
              .articles=${built.articles}
            ></sarah-recap-articles>
          </div>
        </div>

        <div class="legal">
          <p>${unsafeHTML(STRINGS.legal)}</p>
        </div>
      </div>
      ` : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sarah-recap': SarahRecap;
  }
}
